import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setAppEventZIndex, setToggled } from '../../../redux/action/system'
import { addTableCartOrderItem, setTableCartCancelItem, setTableCartCancelSubItem, setTableCartItemFocusIndex, setTableCartItemToDelete, setTableCartNewOrder, setTableCartOrderFocusIndex, setTableCartOrders, setTableCartShowMobileCart } from '../../../redux/action/tableCart'
import { useNavigate } from 'react-router-dom'
import { uuidGenerator } from '../../../helper'
import { jwtContainsAnyGroup, jwtGetUsername, parseJwt } from '../../../helper/jwt'
import { abortTableOrdersThunk, fetchTableOrdersThunk, updateTableCartThunk } from '../../../redux/thunk/restaurant/order'
import ModalLogin from '../../Elements/modalLogin'
import MainWrapper from '../../MainWrapper'
import { selectCancelItems, selectCancelSubItems, selectCartDiscountValue, selectCurrentOrdersInTableCart, selectGiftCouponInTableCart, selectHasOnlyCanceledItemsInRegisteredOrders, selectIsSelectedItemsKitchenConfigMode, selectProcessedBy, selectSelectDishCourseIndex, selectTotalPriceNewOrder } from '../../../redux/selector/restaurant'
import DishCourseModal from '../../Elements/cart/CartItem/CartDishCourse/modal'
import CartWithProducts from './CartWithProducts'
import CartHeader from './Header'
import { fetchRestaurantTableThunk } from '../../../redux/thunk/restaurant/table'
import { useLocalStorage } from '../../../hooks/useLocalStorage'
import { setRestaurantAbortOrderModal, setRestaurantAreaId, setRestaurantShowSendToKitchenModal } from '../../../redux/action/restaurant'
import SendToKitchenModal from '../SendToKitchenModal'
import { getRestaurantSettingsDishCoursesThunk } from '../../../redux/thunk/restaurantSettings'
import { getKitchenConfigThunk } from '../../../redux/thunk/kitchenMonitor'
import { logger } from '../../../logger/logger'
import CancelReasonModal from '../CancelReasonModal'
import CancellationDetailsModal from '../CancellationDetailsModal'
import AbortOrderModal from '../../Elements/modal/order/AbortOrderModal'

const EVENT_Z_INDEX = 0

const TableCart = () => {
	const navigate = useNavigate()
	const orderFocusIndex = useSelector(state => state.tableCart.orderFocusIndex)
	const itemFocusIndex = useSelector(state => state.tableCart.itemFocusIndex)
	const orders = useSelector(selectCurrentOrdersInTableCart)
	const table = useSelector(state => state.restaurant.table)
	const accessToken  = useSelector(state => state.core.auth.accessToken)
	const waiterAccessToken = useSelector(state => state.modalLogin.accessToken)
	const { menu } = useSelector(store => store.cartProduct)
	const dispatch = useDispatch()
	const [adminAccessToken, setAdminAccessToken] = useState(null)
	const [modalLoginIsOpen, setModalLoginIsOpen] = useState(false)
	const userIsNotAdmin = !jwtContainsAnyGroup(accessToken, ['OWNER', 'ADMIN', 'SUPERADMIN'])
	const pinCodeModalLoginIsNeeded = useSelector(state => state.modalLogin.pinCodeModalLoginIsNeeded)
	const modalLoginUsername = useSelector(state => state.modalLogin.username)
	const hasModalLogin = !!waiterAccessToken
	const hasGiftCoupon = useSelector(selectGiftCouponInTableCart)
	const discountType = useSelector(store => store.cartDiscount.discountType)
	const discountValue = useSelector(selectCartDiscountValue(hasGiftCoupon))
	const totalPriceNewOrder = useSelector(selectTotalPriceNewOrder)
	const itemIsRemoving = useSelector(state => state.tableCart.loading)
	const processedBy = useSelector(selectProcessedBy)
	const selectDishCourseIndex = useSelector(selectSelectDishCourseIndex)
	const [tableId, setTableId] = useLocalStorage('tableId', null)
	const [, setRestaurantNewOrder] = useLocalStorage('restaurantNewOrder', null)
	const isSelectedItemsKitchenConfigMode = useSelector(selectIsSelectedItemsKitchenConfigMode)
	const itemsForRemoving = useSelector(selectCancelItems)
	const subItemsForRemoving = useSelector(selectCancelSubItems)
	const hasOnlyCanceledItemsInRegisteredOrders = useSelector(selectHasOnlyCanceledItemsInRegisteredOrders)
	const abortOrderModal = useSelector(state => state.restaurant.abortOrderModal)
	const adminTokenIsValid = adminAccessToken && (new Date(parseJwt(adminAccessToken).exp * 1000)) > new Date()
	const adminName = adminAccessToken ? jwtGetUsername(adminAccessToken) : null
	const newOrder = orders?.find(order => !order.id)
	const userWhoAppliedConfirmation = adminTokenIsValid ? adminName : processedBy
	const hasCanceledItemsOrSubItems = itemsForRemoving?.length > 0 || subItemsForRemoving?.length > 0
	const showSendToKitchenModal = isSelectedItemsKitchenConfigMode && newOrder && !hasGiftCoupon
	const shouldAbortOrder = hasCanceledItemsOrSubItems && !newOrder && hasOnlyCanceledItemsInRegisteredOrders

	const dataForUpdateCartRef = useRef({
		newOrder: null,
		cancelItems: [],
		cancelSubItems: [],
	})

	useEffect(() => {
		dispatch(setAppEventZIndex(EVENT_Z_INDEX))
		dispatch(setToggled(true))

		return () => {
			dispatch(setTableCartOrders([]))
			dispatch(setTableCartNewOrder(null))
			resetCancelItems()
			resetCartDataRef()
			setRestaurantNewOrder(null)
			setTableId(null)
		}
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (tableId) {
			dispatch(fetchRestaurantTableThunk(tableId))
			dispatch(getRestaurantSettingsDishCoursesThunk())
			dispatch(getKitchenConfigThunk())
		}
	}, [tableId]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (table) {
			dispatch(fetchTableOrdersThunk(table?.id))
		}

		return () => {
			dispatch(setRestaurantAreaId(table?.areaId))
		}
	}, [table]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if ((orderFocusIndex === null || itemFocusIndex === null) && orders?.length > 0) {
			const ordersCopy = [...orders]
			const lastOrderWithExistItems = ordersCopy.reverse().find(order => order.items?.length > 0)
			if (lastOrderWithExistItems) {
				dispatch(setTableCartOrderFocusIndex(orders.indexOf(lastOrderWithExistItems)))
				dispatch(setTableCartItemFocusIndex(lastOrderWithExistItems.items.length - 1))
			}
		}
	}, [orders])// eslint-disable-line react-hooks/exhaustive-deps

	const navigateBack = () => {
		navigate('/restaurant')
		dispatch(setTableCartShowMobileCart(false))
	}

	useEffect(() => {
		if (!modalLoginUsername && pinCodeModalLoginIsNeeded) {
			navigateBack()
		}
	}, [modalLoginUsername, pinCodeModalLoginIsNeeded]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (!tableId) {
			navigateBack()
		}
	}, [tableId]) // eslint-disable-line react-hooks/exhaustive-deps

	const handleSuccessRedirect = useCallback(() => {
		navigate('/restaurant')
	}, [navigate])


	const resetCancelItems = useCallback(() => {
		dispatch(setTableCartItemToDelete(null))
		dispatch(setTableCartCancelItem([]))
		dispatch(setTableCartCancelSubItem([]))
	}, [dispatch])

	const resetCartDataRef = useCallback(() => {
		dataForUpdateCartRef.current = {
			newOrder: null,
			cancelItems: [],
			cancelSubItems: [],
		}
	}, [])

	const openAbortOrderModal = useCallback(() => {
		dispatch(setRestaurantAbortOrderModal(true))
	}, [dispatch])

	const onAdminLogin = useCallback(async (adminAccessToken = null, data) => {
		if (itemIsRemoving) return
		if (adminAccessToken) {
			setAdminAccessToken(adminAccessToken)
		}
		const token = adminAccessToken ? adminAccessToken : accessToken
		if (showSendToKitchenModal) {
			dispatch(setRestaurantShowSendToKitchenModal(true))
		} else if (shouldAbortOrder) {
			openAbortOrderModal()
		} else {
			await dispatch(updateTableCartThunk(table?.id, data, hasModalLogin, token, !!adminAccessToken, () => handleSuccessRedirect()))
			resetCancelItems()
			resetCartDataRef()
		}
	}, [dispatch, itemIsRemoving, table?.id, accessToken, hasModalLogin, handleSuccessRedirect, openAbortOrderModal, resetCancelItems, resetCartDataRef, shouldAbortOrder, showSendToKitchenModal])

	const prepareDataForUpdateCart = useCallback((orderItems = []) => {
		const preparedOrder = {
			...newOrder,
			items: orderItems.length ? orderItems : newOrder?.items,
			totalPrice: totalPriceNewOrder,
			dailyDiscount: { value: discountValue, type: discountType },
			processedBy: userWhoAppliedConfirmation,
		}
		const preparedCancelItems = itemsForRemoving?.map(item => ({
			...item,
			dateTime: new Date(),
			userName: userWhoAppliedConfirmation,
		}))
		const preparedCancelSubItems = subItemsForRemoving?.map(item => ({
			...item,
			dateTime: new Date(),
			userName: adminName,
		}))
		dataForUpdateCartRef.current = {
			newOrder: newOrder ? preparedOrder : null,
			cancelItems: preparedCancelItems,
			cancelSubItems: preparedCancelSubItems,
		}
	}, [adminName, discountType, discountValue, itemsForRemoving, newOrder, subItemsForRemoving, totalPriceNewOrder, userWhoAppliedConfirmation])

	const updateCartOrder = useCallback(async (orderItems) => {
		const hasAdminToken = !!adminAccessToken && adminTokenIsValid && userIsNotAdmin
		const token = hasAdminToken ? adminAccessToken : accessToken
		logger.info(`Update cart (create) order for table ${table?.id} on total price ${totalPriceNewOrder} from deviceType: ${navigator.userAgent} and deviceId ${uuidGenerator()}`)
		prepareDataForUpdateCart(orderItems)
		await dispatch(updateTableCartThunk(table?.id, dataForUpdateCartRef?.current, hasModalLogin, token, hasAdminToken, () => handleSuccessRedirect()))
		resetCancelItems()
		resetCartDataRef()
	}, [adminTokenIsValid, userIsNotAdmin, adminAccessToken, accessToken, table?.id, totalPriceNewOrder, dispatch, hasModalLogin, handleSuccessRedirect, resetCancelItems, resetCartDataRef, prepareDataForUpdateCart])

	const handleUpdateCart = useCallback(async () => {
		if (hasCanceledItemsOrSubItems) {
			if (!adminTokenIsValid && userIsNotAdmin) {
				setModalLoginIsOpen(true)
			} else if (adminTokenIsValid && userIsNotAdmin) {
				onAdminLogin(adminAccessToken, dataForUpdateCartRef?.current)
			} else {
				onAdminLogin(null, dataForUpdateCartRef?.current)
			}
		} else {
			if (!menu && newOrder) {
				await updateCartOrder()
			}
		}

	}, [newOrder, adminAccessToken, menu, onAdminLogin, updateCartOrder, adminTokenIsValid, userIsNotAdmin, hasCanceledItemsOrSubItems])

	const submitCart = (orderItems = []) => {
		prepareDataForUpdateCart(orderItems)
		if (showSendToKitchenModal) {
			if ((hasCanceledItemsOrSubItems) && !adminTokenIsValid && userIsNotAdmin) {
				setModalLoginIsOpen(true)
			} else {
				dispatch(setRestaurantShowSendToKitchenModal(true))
			}
		} else {
			handleUpdateCart()
		}
	}

	const createNewOrder = (item) => {
		const itemWithDishCourse = { ...item, dishCourse: selectDishCourseIndex }
		dispatch(setTableCartOrderFocusIndex(null))
		dispatch(setTableCartItemFocusIndex(null))
		dispatch(addTableCartOrderItem(itemWithDishCourse))
	}

	const handleModalLoginClose = () => {
		setModalLoginIsOpen(false)
	}

	const closeAbortOrderModal = useCallback(() => {
		dispatch(setRestaurantAbortOrderModal(false))
	}, [dispatch])

	const handleAbortOrder = useCallback(async (reason, reasonCode) => {
		const reasonResult = reason ? reason : reasonCode
		const result = await dispatch(abortTableOrdersThunk(table?.id, reasonResult, reasonCode, hasModalLogin))
		if (result?.success) {
			resetCancelItems()
			resetCartDataRef()
			handleSuccessRedirect()
		}
	}, [dispatch, table?.id, hasModalLogin, resetCancelItems, resetCartDataRef, handleSuccessRedirect])

	return (
		<>
			<CartHeader {...{ zIndex: EVENT_Z_INDEX }} />
			<MainWrapper>
				<CartWithProducts {...{ zIndex: EVENT_Z_INDEX, submitCart, createNewOrder, hasCanceledItemsOrSubItems }} />
				<ModalLogin isOpen={modalLoginIsOpen}
										close={handleModalLoginClose}
										group="SUPERADMIN"
										title="Als Restaurantadmin anmelden"
										onLogin={(token) => onAdminLogin(token, dataForUpdateCartRef.current)}
										storeAccessToken={false}
										returnZIndex={0} />
				<DishCourseModal />
				<SendToKitchenModal handleUpdateCart={updateCartOrder} tableCart={true} />
				<CancelReasonModal />
				<CancellationDetailsModal />
				<AbortOrderModal isOpen={abortOrderModal}
												 close={closeAbortOrderModal}
												 onAbortOrder={handleAbortOrder} />

			</MainWrapper>
		</>
	)
}

TableCart.displayName = 'TableCart'
export default TableCart
